import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/constants";
import { LuxButton } from "@/components/lux";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const go = (to) => {
    setOpen(false);
    if (!to.includes("#")) { navigate(to); return; }
    const hash = to.split("#")[1];
    const scrollTo = () => {
      const el = document.getElementById(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0, duration: 1.6 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollTo, 600);
    } else {
      scrollTo();
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass border-b border-white/10 py-3" : "py-5 bg-gradient-to-b from-black/60 to-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" onClick={() => go("/#home")} data-testid="logo-link" className="flex items-center">
            <img src={LOGO_URL} alt="LUHAI — Not Your Average Photographer" className="h-8 sm:h-9 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => go(l.to)}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="font-mono2 text-xs tracking-[0.2em] text-white/70 hover:text-[#02AACF] transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#02AACF] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LuxButton size="sm" className="hidden sm:inline-flex" data-testid="nav-book-now" onClick={() => navigate("/book")}>
              BOOK NOW
            </LuxButton>
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[#090A0F] grain lg:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-white/10">
              <img src={LOGO_URL} alt="LUHAI" className="h-8" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="text-white p-2" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1 }}
                  onClick={() => go(l.to)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="text-left font-display font-bold text-4xl text-white py-2 hover:text-[#02AACF] transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <div className="p-8">
              <LuxButton size="lg" className="w-full" data-testid="mobile-book-now" onClick={() => { setOpen(false); navigate("/book"); }}>
                BOOK NOW
              </LuxButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
